import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, CheckCircle2, Users, HandCoins, GraduationCap,
  Landmark, HomeIcon, PiggyBank, Building2, ScrollText, BookOpen,
  ClipboardList, FileCheck2, Repeat, Award
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { useI18n } from "../i18n";

const heroImg = "/images/hero-consultant.jpg";
const familyImg = "/images/family-support.jpg";
const literacyImg = "/images/financial-literacy.jpg";
const consultingImg = "/images/consulting-office.jpg";
const instImg = "/images/institutional.jpg";

const benefitIcons = [HandCoins, Users, GraduationCap, HomeIcon, Landmark, PiggyBank];
const processIcons = [ClipboardList, FileCheck2, ScrollText, Repeat];

export default function Home() {
  const { t, tArr } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (e) => {
    const value = field === "consent" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name || form.name.trim().length < 2) errs.name = t("newsletter.errorName");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = t("newsletter.errorEmail");
    if (!form.consent) errs.consent = t("newsletter.errorConsent");
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      try {
        sessionStorage.setItem("saverondelio_lead", JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          at: new Date().toISOString(),
        }));
      } catch (_) { /* ignore */ }
      navigate("/thank_you");
    }
  };

  const stats = tArr("stats.items");
  const creatives = tArr("creatives.cards");
  const benefits = tArr("benefits.items");
  const steps = tArr("process.steps");
  const testimonials = tArr("testimonials.items");
  const faqs = tArr("faq.items");

  const heroImages = [familyImg, literacyImg, consultingImg];

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-white pt-28 pb-20 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-emerald-50" />
          <div className="absolute top-40 -left-20 w-72 h-72 rounded-full bg-slate-50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 px-3 py-1.5 rounded border border-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
              {t("hero.eyebrow")}
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-slate-900 mt-5" data-testid="hero-title">
              {t("hero.title")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed max-w-2xl">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#newsletter"
                data-testid="hero-cta-button"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-[#006837] text-white font-bold hover:bg-[#004B28]"
              >
                {t("hero.ctaPrimary")}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
              <a
                href="#benefits"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center px-6 py-3.5 rounded-md border-2 border-[#0B2545] text-[#0B2545] font-bold hover:bg-slate-50"
              >
                {t("hero.ctaSecondary")}
              </a>
            </div>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              {["one", "two", "three"].map((k) => (
                <li key={k} className="flex items-start gap-2 text-slate-700">
                  <CheckCircle2 className="w-4 h-4 mt-1 text-[#006837] shrink-0" aria-hidden="true" />
                  <span>{t(`hero.trust.${k}`)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-xl overflow-hidden border-4 border-[#0B2545] shadow-xl">
              <img
                src={heroImg}
                alt={t("hero.imageAlt")}
                className="w-full h-[420px] object-cover"
                loading="eager"
                data-testid="hero-image"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur px-4 py-3 rounded-md border border-slate-200 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#006837] text-white grid place-items-center">
                  <Award className="w-5 h-5" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#0B2545]">
                    {t("header.badge")}
                  </p>
                  <p className="text-xs text-slate-600">RFC {t("footer.rfc")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0B2545] text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold leading-snug">
              {t("stats.title")}
            </h2>
            <p className="mt-3 text-slate-300">{t("stats.subtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={i} className="border border-white/10 rounded-lg p-6 bg-white/5" data-testid={`stat-${i}`}>
                <p className="font-heading text-3xl sm:text-4xl font-extrabold text-[#C59B27]">{s.value}</p>
                <p className="mt-2 text-sm text-slate-200 leading-snug">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CREATIVES / EDUCATION */}
      <section id="education" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 px-3 py-1 rounded border border-emerald-200">
              {t("creatives.eyebrow")}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4 text-slate-900">
              {t("creatives.title")}
            </h2>
            <p className="mt-3 text-slate-700">{t("creatives.subtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
            {creatives.map((card, i) => (
              <article key={i} className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col" data-testid={`creative-card-${i}`}>
                <img src={heroImages[i]} alt={card.imageAlt} className="w-full h-56 object-cover" loading="lazy" />
                <div className="p-6 flex-1 flex flex-col">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#006837]">{card.kicker}</span>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-slate-900 leading-snug">{card.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{card.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="bg-slate-50 py-16 md:py-24 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 px-3 py-1 rounded border border-emerald-200">
              {t("benefits.eyebrow")}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4 text-slate-900">
              {t("benefits.title")}
            </h2>
            <p className="mt-3 text-slate-700">{t("benefits.subtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => {
              const Icon = benefitIcons[i % benefitIcons.length];
              return (
                <div key={i} className="p-6 bg-white border border-slate-200 rounded-lg" data-testid={`benefit-${i}`}>
                  <div className="w-11 h-11 rounded-md bg-emerald-50 text-[#006837] grid place-items-center border border-emerald-200">
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 px-3 py-1 rounded border border-emerald-200">
              {t("process.eyebrow")}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4 text-slate-900">
              {t("process.title")}
            </h2>
            <p className="mt-3 text-slate-700">{t("process.subtitle")}</p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {steps.map((s, i) => {
              const Icon = processIcons[i % processIcons.length];
              return (
                <div key={i} className="p-6 rounded-lg border border-slate-200 bg-slate-50" data-testid={`step-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-heading text-2xl font-extrabold text-[#C59B27]">{s.number}</span>
                    <div className="w-9 h-9 rounded-md bg-[#0B2545] text-white grid place-items-center">
                      <Icon className="w-4 h-4" aria-hidden="true" />
                    </div>
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-50 py-16 md:py-24 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 px-3 py-1 rounded border border-emerald-200">
              {t("testimonials.eyebrow")}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4 text-slate-900">
              {t("testimonials.title")}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((tm, i) => (
              <figure key={i} className="p-6 bg-white border border-slate-200 rounded-lg flex flex-col" data-testid={`testimonial-${i}`}>
                <blockquote className="text-slate-700 text-sm leading-relaxed">“{tm.quote}”</blockquote>
                <figcaption className="mt-6">
                  <p className="font-semibold text-slate-900 text-sm">{tm.name}</p>
                  <p className="text-xs text-slate-500">{tm.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER / LEAD FORM */}
      <section id="newsletter" className="relative py-16 md:py-24 bg-[#0B2545] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src={instImg} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#C59B27]">
              {t("newsletter.eyebrow")}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-3 leading-snug">
              {t("newsletter.title")}
            </h2>
            <p className="mt-4 text-slate-200 text-sm leading-relaxed">{t("newsletter.subtitle")}</p>
            <ul className="mt-6 space-y-2 text-sm">
              {["one", "two", "three"].map((k) => (
                <li key={k} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#C59B27] shrink-0" aria-hidden="true" />
                  <span>{t(`hero.trust.${k}`)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              noValidate
              data-testid="newsletter-form"
              className="bg-white text-slate-900 rounded-xl p-6 md:p-8 border border-slate-200 shadow-xl"
            >
              <div className="grid grid-cols-1 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-800">
                    {t("newsletter.nameLabel")}
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange("name")}
                    placeholder={t("newsletter.namePlaceholder")}
                    data-testid="newsletter-name-input"
                    className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#006837]"
                    autoComplete="name"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-600" data-testid="error-name">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-800">
                    {t("newsletter.emailLabel")}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder={t("newsletter.emailPlaceholder")}
                    data-testid="newsletter-email-input"
                    className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#006837]"
                    autoComplete="email"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-600" data-testid="error-email">{errors.email}</p>}
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={handleChange("consent")}
                    data-testid="newsletter-consent"
                    className="mt-1 w-4 h-4 accent-[#006837]"
                  />
                  <span>{t("newsletter.consent")}</span>
                </label>
                {errors.consent && <p className="text-xs text-red-600" data-testid="error-consent">{errors.consent}</p>}
                <button
                  type="submit"
                  data-testid="newsletter-submit-button"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-[#006837] text-white font-bold hover:bg-[#004B28]"
                >
                  {t("newsletter.submit")}
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
                <p className="text-xs text-slate-500 leading-relaxed">{t("newsletter.disclaimer")}</p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-50 px-3 py-1 rounded border border-emerald-200">
            {t("faq.eyebrow")}
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4 text-slate-900">
            {t("faq.title")}
          </h2>
          <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
            {faqs.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-slate-50 py-16 md:py-20 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{t("cta.title")}</h2>
            <p className="mt-2 text-slate-700">{t("cta.subtitle")}</p>
          </div>
          <a
            href="#newsletter"
            data-testid="final-cta-button"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-[#006837] text-white font-bold hover:bg-[#004B28] shrink-0"
          >
            {t("cta.button")}
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </section>
    </main>
  );
}
